import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Brain, GoogleLogo, UserPlus, Briefcase, Buildings } from "@phosphor-icons/react";

export default function Register() {
    const { register } = useAuth();
    const nav = useNavigate();
    const [params] = useSearchParams();
    const [role, setRole] = useState(params.get("role") === "company" ? "company" : "seeker");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await register({ email, password, name, role, company_name: companyName || null });
            toast.success("Account created. Welcome to Jobs IQ.");
            nav("/dashboard");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Sign-up failed");
        } finally {
            setLoading(false);
        }
    };

    const googleSignup = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/dashboard";
        sessionStorage.setItem("jobsiq_google_role", role);
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <div className="min-h-screen grid md:grid-cols-2" data-testid="register-page">
            <div className="flex items-center justify-center p-8 order-2 md:order-1">
                <form onSubmit={submit} className="w-full max-w-sm" data-testid="register-form">
                    <Link to="/" className="md:hidden font-display text-2xl mb-6 inline-block">
                        Jobs<span className="text-blue-600">IQ</span>
                    </Link>
                    <h2 className="font-display text-3xl">Create account</h2>
                    <p className="text-sm text-slate-600 mt-2">
                        Already a member?{" "}
                        <Link to="/login" data-testid="link-to-login" className="text-blue-600 hover:underline">
                            Sign in
                        </Link>
                    </p>

                    <div className="mt-6 grid grid-cols-2 gap-2" role="radiogroup">
                        <button
                            type="button"
                            data-testid="role-seeker-toggle"
                            onClick={() => setRole("seeker")}
                            className={`p-3 border text-left ${role === "seeker" ? "border-slate-950 bg-slate-950 text-white" : "border-slate-300 hover:border-slate-950"}`}
                        >
                            <Briefcase size={18} weight="bold" />
                            <div className="font-display text-lg mt-1">I'm a job seeker</div>
                            <div className="text-xs opacity-70">Profile · apply · AI coach</div>
                        </button>
                        <button
                            type="button"
                            data-testid="role-company-toggle"
                            onClick={() => setRole("company")}
                            className={`p-3 border text-left ${role === "company" ? "border-slate-950 bg-slate-950 text-white" : "border-slate-300 hover:border-slate-950"}`}
                        >
                            <Buildings size={18} weight="bold" />
                            <div className="font-display text-lg mt-1">I'm hiring</div>
                            <div className="text-xs opacity-70">Post · rank · review</div>
                        </button>
                    </div>

                    <button
                        type="button"
                        onClick={googleSignup}
                        data-testid="google-signup-btn"
                        className="mt-6 w-full flex items-center justify-center gap-2 border border-slate-300 py-3 hover:bg-slate-950 hover:text-white hover:border-slate-950 transition-colors"
                    >
                        <GoogleLogo size={18} weight="bold" /> Continue with Google
                    </button>

                    <div className="flex items-center gap-3 my-5 text-xs font-mono-plex uppercase tracking-widest text-slate-500">
                        <div className="flex-1 h-px bg-slate-200" />
                        or with email
                        <div className="flex-1 h-px bg-slate-200" />
                    </div>

                    <label className="block text-xs font-mono-plex uppercase tracking-widest text-slate-500 mb-1">
                        {role === "company" ? "Your name" : "Full name"}
                    </label>
                    <input
                        data-testid="register-name-input"
                        required value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full px-3 py-3 border border-slate-300 focus:border-blue-600 focus:outline-none"
                    />

                    {role === "company" && (
                        <>
                            <label className="block text-xs font-mono-plex uppercase tracking-widest text-slate-500 mt-4 mb-1">
                                Company name
                            </label>
                            <input
                                data-testid="register-company-input"
                                required value={companyName}
                                onChange={(e) => setCompanyName(e.target.value)}
                                className="w-full px-3 py-3 border border-slate-300 focus:border-blue-600 focus:outline-none"
                            />
                        </>
                    )}

                    <label className="block text-xs font-mono-plex uppercase tracking-widest text-slate-500 mt-4 mb-1">
                        Email
                    </label>
                    <input
                        data-testid="register-email-input"
                        type="email" required value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-3 py-3 border border-slate-300 focus:border-blue-600 focus:outline-none"
                    />

                    <label className="block text-xs font-mono-plex uppercase tracking-widest text-slate-500 mt-4 mb-1">
                        Password
                    </label>
                    <input
                        data-testid="register-password-input"
                        type="password" required minLength={6} value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full px-3 py-3 border border-slate-300 focus:border-blue-600 focus:outline-none"
                    />

                    <button
                        data-testid="register-submit-btn"
                        disabled={loading}
                        className="mt-6 w-full bg-slate-950 text-white py-3 hover:bg-blue-600 disabled:opacity-60 inline-flex items-center justify-center gap-2"
                    >
                        <UserPlus size={18} weight="bold" />
                        {loading ? "Creating…" : "Create account"}
                    </button>
                </form>
            </div>

            <div className="hidden md:flex bg-slate-950 text-white p-12 flex-col justify-between order-1 md:order-2 relative overflow-hidden">
                <div className="absolute inset-0 grid-lines opacity-40" />
                <Link to="/" className="relative flex items-center gap-2 self-end">
                    <span className="font-display text-xl">
                        Jobs<span className="text-blue-400">IQ</span>
                    </span>
                    <div className="w-8 h-8 bg-white flex items-center justify-center">
                        <Brain size={20} color="#0B132B" weight="duotone" />
                    </div>
                </Link>
                <div className="relative">
                    <h1 className="font-display text-5xl leading-tight">
                        One profile.
                        <br />
                        A thousand possibilities.
                    </h1>
                    <p className="mt-6 text-slate-400 max-w-md">
                        Whether you're hiring or hired, Jobs IQ gives you the vocabulary to describe what you actually do well.
                    </p>
                </div>
                <div className="relative font-mono-plex text-xs uppercase tracking-widest text-slate-500">
                    No credit card · 2 minutes · Cancel anytime
                </div>
            </div>
        </div>
    );
}
